import { Box, Breadcrumbs, Card, EmptyState, Page, Table, Text, TextButton } from '@wix/design-system'
import React, { useMemo } from 'react'
import { useNavigation } from '../context/NavigationContext'
import { useFirebaseDoc, useFirebaseQueryOptions } from '../hooks/firebaseHooks'
import { Collections, FREE_RESPONSE_LIMIT } from '../utils/shared/constants'
import { Campaign } from '../models/shared/Campaign'
import { AnalyticsDateRange, DEFAULT_DATE_RANGE } from '../models/AnalyticsDateRange'
import DateRangePicker from '../components/DateRangePicker'
import { ResponseModel } from '../models/shared/ResponseModel'
import { where } from 'firebase/firestore'
import { calculateResults, redirectToUpgrade } from '../utils/logic'
import NPSDisplay from '../components/NPSDisplay'
import UserAgentDisplay from '../components/UserAgentDisplay'
import AnalyticsHighlights from '../components/AnalyticsHighlights'
import { useInstanceContext } from '../context/InstanceContext'

interface Props { }


function ResponsesPage(props: Props) {
    const { args, navigate } = useNavigation()
    const campaignId = args.id;
    const [campaign] = useFirebaseDoc<Campaign>(`${Collections.campaigns}/${campaignId}`)
    const [dateRange, setDateRange] = React.useState<AnalyticsDateRange>(DEFAULT_DATE_RANGE);
    const [responses] = useFirebaseQueryOptions<ResponseModel>(`${Collections.campaigns}/${campaignId}/${Collections.responses}`,
        {
            queryConstraints: [
                where('createdAt', '>=', dateRange.compareFrom),
                where('createdAt', '<=', dateRange.to)
            ],
            dependencies: [dateRange.compareFrom, dateRange.to]
        }
    )
    const { instanceData, instanceId } = useInstanceContext();
    const responseResults = useMemo(() => calculateResults(responses, dateRange), [responses, dateRange]);


    let finalItems = responseResults.items;
    if (!instanceData.planId && responseResults.items.length > FREE_RESPONSE_LIMIT) {
        finalItems = responseResults.items.slice(0, FREE_RESPONSE_LIMIT);
    }

    return <Page height="100vh">
        <Page.Header
            title="Responses"
            subtitle={campaign?.name || 'Loading...'}
            breadcrumbs={
                <Breadcrumbs
                    activeId="responses"
                    items={[
                        { id: 'home', value: 'Dashboard' },
                        { id: 'responses', value: 'Responses', disabled: true },
                    ]}
                    onClick={() => navigate('home')}
                />
            }
            showBackButton={true}
            onBackClicked={() => navigate('home')}
        />

        <Page.Content>
            <Box gap={'24px'} direction='vertical'>
                <Card className='py-4 px-3'>
                    <DateRangePicker dateRange={dateRange} setDateRange={setDateRange} />
                </Card>

                <Card className='p-4'>
                    <AnalyticsHighlights responseResults={responseResults} />
                </Card>

                <Card className='pb-4'>
                    <Card.Header title='Details' />

                    {!instanceData.planId && <Box paddingLeft="SP4" paddingBottom="SP1">
                        <Text>On free plan only first {FREE_RESPONSE_LIMIT} responses are shown.
                            <Box inline paddingLeft="SP1">
                                <TextButton onClick={() => redirectToUpgrade(instanceId)} >Upgrade to see all</TextButton>
                            </Box>
                        </Text>
                    </Box>}

                    <Table
                        data={finalItems}

                        columns={[
                            { title: 'NPS', render: item => <NPSDisplay response={item} /> },
                            { title: 'Comment', render: item => item.comment },
                            { title: 'Device', render: item => <UserAgentDisplay userAgent={item.userAgent} /> },
                            { title: 'Created At', render: item => item.createdAt.toLocaleString() },
                        ]}
                    />

                    {responseResults.items.length === 0 && <EmptyState
                        title="No data"
                        subtitle="No responses found for the selected date range."
                    >
                    </EmptyState>}

                </Card>
            </Box>

        </Page.Content>
    </Page>
}

export default ResponsesPage
